import { useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { postFleetHalt } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { AlertOctagon } from 'lucide-react';

interface HaltModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export function HaltModal({ open, onClose, onComplete }: HaltModalProps) {
  const [operatorId, setOperatorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputFocused, setInputFocused] = useState(false);

  if (!open) return null;

  const handleHalt = async () => {
    if (!operatorId.trim()) {
      setError('Operator ID is required.');
      return;
    }
    setLoading(true);
    setError(null);
    const result = await postFleetHalt(operatorId.trim(), 'Sovereign override from Command Center dashboard');
    if (result?.error) {
      setError(result.error);
      setLoading(false);
    } else {
      setOperatorId('');
      setLoading(false);
      onComplete();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="halt-modal-title"
    >
      <div className="bg-[#292524] border border-[#3A352F] rounded-lg p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <AlertOctagon aria-hidden="true" className="w-6 h-6" style={{ color: GovernanceColors.critical }} />
          <h3 id="halt-modal-title" className="text-lg font-semibold" style={{ color: GovernanceColors.critical }}>Confirm Emergency Halt</h3>
        </div>
        <p className="text-sm text-[#A8A29E] mb-4">
          This will immediately halt all active agents. All operations will be suspended.
          This action is logged in the audit trail.
        </p>
        <div className="mb-4">
          <label htmlFor="halt-operator-id" className="block text-xs text-[#A8A29E] mb-1">Operator ID (required)</label>
          <input
            id="halt-operator-id"
            type="text"
            value={operatorId}
            onChange={(e) => setOperatorId(e.target.value)}
            placeholder="Enter your operator ID"
            className="w-full px-3 py-2 bg-[#1C1917] border rounded text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none"
            style={{ borderColor: inputFocused ? GovernanceColors.primary : '#3A352F' }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            autoFocus
          />
        </div>
        {error && <p className="text-xs mb-3" style={{ color: GovernanceColors.critical }}>{error}</p>}
        <div className="flex gap-3">
          <Button
            onClick={handleHalt}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white flex-1"
          >
            {loading ? 'Halting...' : 'Confirm Halt'}
          </Button>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outline"
            className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3D3833] flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
